import type { WorkOrderResultViewData } from 'gql/graphql';
import { SectionEnum } from 'gql/graphql';
import { formatActTitle } from 'pages/Project/Common/ActKc2Title/ActKc2Title';
import { WorkOrderChapters } from 'pages/Project/UerProject/WorkOrder/workorderTypes';

import { useMemo } from 'react';

import { getFindKey, getSeasonAccessor } from '../helpers';

export const chapterSectionsSequence = [
    WorkOrderChapters.CHAPTER1_7,
    WorkOrderChapters.CHAPTER8,
    WorkOrderChapters.CHAPTER9,
    SectionEnum.CustomerLimits,
    SectionEnum.Others,
    SectionEnum.WorkOrderTotals,
];

export const chapterOrSectionTitle: Record<string, string> = {
    [WorkOrderChapters.CHAPTER1_7]: 'Работы Глав 1-7 + МТР',
    [WorkOrderChapters.CHAPTER8]: 'Работы 8 Главы + МТР',
    [WorkOrderChapters.CHAPTER9]: 'Работы 9 Главы + МТР',
    [SectionEnum.CustomerLimits]: 'Лимиты Заказчика',
    [SectionEnum.Others]: 'Прочие',
    [SectionEnum.WorkOrderTotals]: 'Итого',
};

export enum OrderSummaryTableRowType {
    DATA = 'DATA',
    SUBHEADER = 'SUBHEADER',
}

export interface OrderSummaryTableRow {
    title: string;
    totalPlanCost?: number;
    totalCompletedCost?: number;
    totalRemainderCost?: number;
    [key: ReturnType<typeof getSeasonAccessor>]: number;
    [key: ReturnType<typeof formatActTitle>]: number;
    type: OrderSummaryTableRowType;
}

export const useOrderSummaryTableRows = (data: WorkOrderResultViewData): [OrderSummaryTableRow[], number] => {
    const { results = [], actKC2s = [], seasons = [] } = data || {};

    return useMemo(() => {
        const rows: OrderSummaryTableRow[] = [];
        const dataByChaptersOrSequences = chapterSectionsSequence.map((key) => ({
            key,
            items: results.filter((s) => getFindKey(s) === key),
        }));

        let groupsAvailable = 0;

        dataByChaptersOrSequences.forEach((chOrSData) => {
            if (chOrSData.items.length) {
                const groupTitle = chapterOrSectionTitle[chOrSData.key];
                const { items } = chOrSData;

                groupsAvailable += 1;
                rows.push({ title: groupTitle, type: OrderSummaryTableRowType.SUBHEADER });

                items.forEach((item) => {
                    const {
                        title,
                        totalPlanCost,
                        totalCompletedCost,
                        totalRemainderCost,
                        seasonResults,
                        actKC2Results,
                    } = item;
                    const row: OrderSummaryTableRow = {
                        title,
                        type: OrderSummaryTableRowType.DATA,
                        totalPlanCost,
                        totalCompletedCost,
                        totalRemainderCost,
                    };

                    seasons.forEach((season) => {
                        const { id } = season;
                        const seasonData = seasonResults.find((d) => d.seasonId === id);

                        if (seasonData) {
                            const seasonColumnKey = getSeasonAccessor(id);

                            row[seasonColumnKey] = seasonData.value;
                        }
                    });

                    actKC2s.forEach((act) => {
                        const { id, number, date } = act;
                        const actData = actKC2Results.find((d) => d.actKC2Id === id);

                        if (actData) {
                            const actColumnKey = formatActTitle(number, date);

                            row[actColumnKey] = actData.value;
                        }
                    });

                    rows.push(row);
                });
            }
        });

        return [rows, groupsAvailable];
    }, [results, seasons, actKC2s]);
};
